import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { updatePropertyType } from "../Services/APIService";

interface viewDatas {
  typeId: number;
  typeName: string;
  isActive: boolean;
}

interface UpdateCardProps {
  viewData: viewDatas | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  typeId: number | "";
  typeName: string;
  isActive: boolean;
}

// API service function for updating access permission

const UpdateCard: React.FC<UpdateCardProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    typeId: "",
    typeName: "",
    isActive: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requiredField = <span className="text-danger ms-1">*</span>;

  // Set form data when accessPermissionData changes
  useEffect(() => {
    if (viewData) {
      setFormData({
        typeId: viewData.typeId,
        typeName: viewData.typeName,
        isActive: viewData.isActive,
      });
    }
  }, [viewData]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (formData.typeId === "" || isNaN(Number(formData.typeId))) {
      setError("Please enter a valid Permission ID");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID and tenant ID from localStorage
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      // Prepare request payload
      const payload = {
        typeId: Number(formData.typeId),
        typeName: formData.typeName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        updatedBy: parseInt(userId),
      };

      const result = await updatePropertyType(payload);

      if (result.data.isUpdated) {
        toast.success("Property Type Updated Successfully!");
        // Notify parent component that update is complete
        onUpdateComplete();
      } else {
        toast.error(result.data.message || "Property Type update failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setError(null);
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update Property Type</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="typeId" className="form-label">
                Type ID
              </label>
              <input
                type="number"
                id="typeId"
                name="typeId"
                className="form-control form-control-lg"
                placeholder="Type ID"
                value={formData.typeId}
                disabled // Disable the input field for typeId
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="typeName" className="form-label">
                Type Name{requiredField}
              </label>
              <input
                type="text"
                id="typeName"
                name="typeName"
                className="form-control form-control-lg"
                placeholder="Type Name"
                value={formData.typeName}
                onChange={(e) =>
                  setFormData({ ...formData, typeName: e.target.value })
                }
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <span className="form-check-label">IsActive</span>
              </label>
            </div>

            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCard;
